/* eslint-disable max-lines */
import React, {ChangeEventHandler, ComponentProps, useCallback, useMemo} from "react";
import {gql} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {useFormState} from "../../hooks/useFormState";
import useVisibility from "../../hooks/useVisibility";

import {Project, EducationMemberFilterInput, Group, Challenge, ParticipationState} from "../../schema";
import {BooleanForm, booleanFormEnumToValue, booleanFormValueToEnum} from "../../utils";
import without from "lodash/without";

import {HelpCircleIcon} from "../../ui/icons";
import Input from "../../ui/input";
import Select from "../../ui/select";
import WithTooltip from "../../ui/tooltip";
import Checkbox from "../../ui/checkbox";
import Link from "../../ui/link";

import classes from "./MembersFilterForm.module.css";
import UserEmotionSelect from "./UserEmotionSelect";

export type QueryData = {
  challenges: Pick<Challenge, "id" | "name">[];
  projects: Pick<Project, "id" | "name">[],
  groups: Pick<Group, "id" | "name">[];
};

type Props = Omit<ComponentProps<"form">, "children" | "onSubmit"> & {
  challenges?: QueryData["challenges"];
  projects?: QueryData["projects"],
  groups?: QueryData["groups"];
  initialValues?: EducationMemberFilterInput;
  onSave: (data: EducationMemberFilterInput) => void;
};

type formState = {
  email: EducationMemberFilterInput["email"];
  username: EducationMemberFilterInput["username"];
  active: BooleanForm;
  challengeId: EducationMemberFilterInput["challengeId"];
  projectId: EducationMemberFilterInput["projectId"],
  groupsIds: NonNullable<EducationMemberFilterInput["groupsIds"]>;
  completed: BooleanForm;
  daysAmount: EducationMemberFilterInput["daysAmount"];
  adminFilter: BooleanForm;
  emotion: EducationMemberFilterInput["emotion"];
  participationState: EducationMemberFilterInput["participationState"];
};

export default function MembersFilterForm({initialValues, challenges, projects, groups, onSave, ...props}: Props) {
  const {t} = useTranslation();

  const [allGroupsVisible, showAllGroups, showLessGroups] = useVisibility();

  const formState = useFormState<formState>({
    initialValues: {
      email: initialValues?.email,
      username: initialValues?.username,
      active: booleanFormValueToEnum(initialValues?.active ?? undefined),
      challengeId: initialValues?.challengeId,
      projectId: initialValues?.projectId,
      groupsIds: initialValues?.groupsIds ?? [],
      completed: booleanFormValueToEnum(initialValues?.completed ?? undefined),
      daysAmount: initialValues?.daysAmount ?? 0,
      adminFilter: booleanFormValueToEnum(initialValues?.adminFilter ?? undefined),
      emotion: initialValues?.emotion,
      participationState: initialValues?.participationState,
    },
    preventDefault: true,
    onChange: (data) => {
      onSave &&
        onSave({
          ...data,
          active: booleanFormEnumToValue(data.active),
          completed: booleanFormEnumToValue(data.completed),
          challengeId: data.challengeId === "0" ? undefined : data.challengeId,
          projectId: data.projectId === "0" ? undefined : data.projectId,
          daysAmount: data.daysAmount ?? 0,
          adminFilter: booleanFormEnumToValue(data.adminFilter),
        });
    },
  });

  const onAdminsSelect = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const checked = e.currentTarget.checked;
      formState.setValues(() => ({
        adminFilter: checked ? BooleanForm.TRUE : BooleanForm.FALSE,
      }));
    },
    [formState],
  );

  const onGroupSelect = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const groupId = e.currentTarget.getAttribute("data-group-id")!;
      const checked = e.currentTarget.checked;
      formState.setValues((values) => ({
        groupsIds: checked ? values.groupsIds.concat(groupId) : without(values.groupsIds, groupId),
      }));
    },
    [formState],
  );

  const selectedGroups = useMemo(() => {
    if (!groups) {
      return;
    }
    if (!allGroupsVisible && groups.length > 5) {
      return groups.slice(0, 5);
    }
    return groups;
  }, [allGroupsVisible, groups]);

  return (
    <form method="post" onSubmit={formState.submitHandler} {...props}>
      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.email.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.email.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip className={classes.help} as="span" helpText={t("components.MemberRowFilterForm.email.helpText")}>
          <Input
            name="email"
            displaySize="sm"
            placeholder="example@gmail.com"
            value={formState.values.email ?? undefined}
            onChange={formState.changeHandler}
          />
        </WithTooltip>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.username.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.username.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.MemberRowFilterForm.username.helpText")}
        >
          <Input
            name="username"
            displaySize="sm"
            placeholder="John Smith"
            value={formState.values.username ?? undefined}
            onChange={formState.changeHandler}
          />
        </WithTooltip>
      </div>
      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.active.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.active.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip className={classes.help} as="span" helpText={t("components.MemberRowFilterForm.active.helpText")}>
          <Select
            name="active"
            value={formState.values.active ?? BooleanForm.UNDEFINED}
            onChange={formState.changeHandler}
          >
            <option value={BooleanForm.UNDEFINED}>{t("components.MemberRowFilterForm.active.no")}</option>
            <option value={BooleanForm.TRUE}>{t("components.MemberRowFilterForm.active.true")}</option>
            <option value={BooleanForm.FALSE}>{t("components.MemberRowFilterForm.active.false")}</option>
          </Select>
        </WithTooltip>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.useremotion.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.useremotion.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <UserEmotionSelect name="emotion" value={formState.values.emotion!} onChange={formState.changeHandler} />
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.challenge.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.challenge.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.MemberRowFilterForm.challenge.helpText")}
        >
          <Select
            name="challengeId"
            value={formState.values.challengeId ?? undefined}
            onChange={formState.changeHandler}
          >
            <option value={0}>{t("components.MemberRowFilterForm.challenge.no")}</option>

            {challenges &&
              challenges.map((challenge) => (
                <option key={challenge.id} value={challenge.id} translate="no">
                  {challenge.name}
                </option>
              ))}
          </Select>
        </WithTooltip>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.project.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.project.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.MemberRowFilterForm.project.helpText")}
        >
          <Select name="projectId" value={formState.values.projectId ?? undefined} onChange={formState.changeHandler}>
            <option value={0}>{t("components.MemberRowFilterForm.project.no")}</option>

            {projects &&
              projects.map((project) => (
                <option key={project.id} value={project.id} translate="no">
                  {project.name}
                </option>
              ))}
          </Select>
        </WithTooltip>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.completed.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.completed.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.MemberRowFilterForm.completed.helpText")}
        >
          <Select
            name="completed"
            value={formState.values.completed ?? BooleanForm.UNDEFINED}
            onChange={formState.changeHandler}
          >
            <option value={BooleanForm.UNDEFINED}>{t("components.MemberRowFilterForm.completed.no")}</option>
            <option value={BooleanForm.TRUE}>{t("components.MemberRowFilterForm.completed.true")}</option>
            <option value={BooleanForm.FALSE}>{t("components.MemberRowFilterForm.completed.false")}</option>
          </Select>
        </WithTooltip>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.participationState.label")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.MemberRowFilterForm.participationState.helpText")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          className={classes.help}
          as="span"
          helpText={t("components.MemberRowFilterForm.participationState.helpText")}
        >
          <Select
            name="participationState"
            value={formState.values.participationState ?? undefined}
            onChange={formState.changeHandler}
          >
            <option value={ParticipationState.ALL}>
              {t("components.MemberRowFilterForm.participationState.all")}
            </option>
            <option value={ParticipationState.ACTIVE}>
              {t("components.MemberRowFilterForm.participationState.active")}
            </option>
            <option value={ParticipationState.ARCHIVED}>
              {t("components.MemberRowFilterForm.participationState.archived")}
            </option>
            <option value={ParticipationState.DELAYED}>
              {t("components.MemberRowFilterForm.participationState.delayed")}
            </option>
          </Select>
        </WithTooltip>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.MemberRowFilterForm.time.label")}{" "}
          <WithTooltip className={classes.help} as="span" helpText={t("components.MemberRowFilterForm.time.helpText")}>
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip className={classes.help} as="span" helpText={t("components.MemberRowFilterForm.time.helpText")}>
          <Select name="daysAmount" value={formState.values.daysAmount ?? 0} onChange={formState.changeHandler}>
            <option value={0}>{t("components.MemberRowFilterForm.time.all")}</option>
            <option value={365}>{t("components.MemberRowFilterForm.time.year")}</option>
            <option value={90}>{t("components.MemberRowFilterForm.time.3months")}</option>
            <option value={30}>{t("components.MemberRowFilterForm.time.month")}</option>
            <option value={7}>{t("components.MemberRowFilterForm.time.week")}</option>
            <option value={1}>{t("components.MemberRowFilterForm.time.24h")}</option>
          </Select>
        </WithTooltip>
      </div>

      {groups && groups?.length > 0 && (
        <div className={classes.formItem}>
          <label className={classes.label}>
            {t("components.MemberRowFilterForm.groups.label")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.MemberRowFilterForm.groups.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <label key={"adminFilter"} className={classes.checkboxLabel}>
            <Checkbox checked={formState.values.adminFilter === BooleanForm.TRUE} onChange={onAdminsSelect} />
            <span className={classes.checkboxText}>{t("components.MemberRowFilterForm.admins")}</span>
          </label>
          {selectedGroups?.map(({name, id}) => (
            <label key={id} className={classes.checkboxLabel}>
              <Checkbox
                checked={formState.values.groupsIds.includes(id)}
                onChange={onGroupSelect}
                data-group-id={id}
              />
              <span className={classes.checkboxText}>{name}</span>
            </label>
          ))}
          {groups &&
            groups.length > 5 &&
            (allGroupsVisible ? (
              <Link onClick={showLessGroups}>{t("components.MemberRowFilterForm.groups.showLess")}</Link>
            ) : (
              <Link onClick={showAllGroups}>{t("components.MemberRowFilterForm.groups.showAll")}</Link>
            ))}
        </div>
      )}
    </form>
  );
}

MembersFilterForm.fragments = {
  challenges: gql`
    fragment MembersFilterFormChallenges on Challenge {
      id
      name
    }
  `,
  projects: gql`
    fragment MembersFilterFormProjects on Project {
      id
      name
    }
  `,
  groups: gql`
    fragment MembersFilterFormGroups on Group {
      id
      name
    }
  `,
};
